import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FindYourStayCTA from "@/components/common/FindYourStayCTA";
import StructuredData, { faqSchema, breadcrumbSchema } from "@/components/common/StructuredData";

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/faqs", { params: { scope: "global" } }).then(({ data }) => setFaqs(data));
  }, []);

  // Group into sections preserving the order they were added.
  const groups = useMemo(() => {
    const map = new Map();
    faqs.forEach((f) => {
      const g = f.group || "General";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(f);
    });
    return Array.from(map.entries()).map(([title, items]) => ({ title, items }));
  }, [faqs]);

  // Map FAQ shape (question/answer) to the StructuredData helper's
  // expected format and dedupe so duplicate questions don't pollute the
  // snippet.
  const schemaItems = useMemo(() =>
    faqs.map((f) => ({ q: f.question, a: f.answer })).filter((it) => it.q && it.a),
    [faqs]);

  return (
    <div data-testid="faq-page" className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-10">
      <Helmet>
        <title>FAQ · Vyagram Experiences | Bookings, stays, shoots, payments</title>
        <meta name="description" content="Answers to common Vyagram questions — bookings, payments, stays, shoots, gatherings, cancellations and check-in." />
        <link rel="canonical" href="https://vyagram.com/faq" />
      </Helmet>
      {schemaItems.length > 0 && (
        <StructuredData
          id="faq-schema"
          data={[
            faqSchema(schemaItems),
            breadcrumbSchema([
              { name: "Home", url: "https://vyagram.com/" },
              { name: "FAQ", url: "https://vyagram.com/faq" },
            ]),
          ]}
        />
      )}

      <p className="vyg-eyebrow">Frequently asked</p>
      <h1 className="mt-3 font-display text-4xl sm:text-6xl">The details, simply put.</h1>

      {/* SEO-crawlable copy: search engines parse this <details> tree even
          before our React accordion hydrates, so every Q&A is visible to
          bots that don't run JavaScript. The visible accordion below is
          purely for the visual experience. */}
      <noscript>
        <div className="mt-10 space-y-8" data-testid="faq-noscript-fallback">
          {groups.map((g) => (
            <div key={`ns-${g.title}`}>
              <h2 className="font-display text-2xl text-forest">{g.title}</h2>
              <dl className="mt-3 space-y-4">
                {g.items.map((x) => (
                  <div key={x.id}>
                    <dt className="font-editorial text-lg text-noir">{x.question}</dt>
                    <dd className="mt-1 editorial-body text-noir/80">{x.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </noscript>

      <div className="mt-12 space-y-10">
        {groups.length === 0 && <p className="text-noir/60">Loading…</p>}
        {groups.map((g, i) => (
          <div key={g.title}>
            <h2 className="font-display text-2xl text-forest">{g.title}</h2>
            <Accordion type="single" collapsible className="mt-4">
              {g.items.map((x, j) => (
                <AccordionItem key={x.id} value={`${i}-${j}`}>
                  <AccordionTrigger className="font-editorial text-lg">{x.question}</AccordionTrigger>
                  {/*
                    AccordionContent collapses display to none when closed.
                    We wrap it in a real <p> so the answer text remains in
                    the rendered HTML for crawlers and screen readers, even
                    if the visual state is currently closed.
                  */}
                  <AccordionContent className="editorial-body" data-testid={`faq-answer-${x.id}`}>{x.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
      <FindYourStayCTA />
    </div>
  );
}
